import { useState, useEffect } from 'react';
import { Avatar, Box, TextField, Typography, Paper, MenuItem, Select, FormControl, InputLabel, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Divider, Grid2, Backdrop, CircularProgress } from '@mui/material';
import { getUnitDetails } from '../../service/service'; // Assuming you have this service call
import { useParams } from 'react-router-dom';
import VoiceAudioControl from '../../components/VoiceAudioControl';
import { getCourseVoiceEntries } from '../../utils/courseVoices';
import Layout from '../../components/Layout';
import { ThemeProvider } from '@mui/material/styles';
import { ViewOnlyPages } from '../../theme';

const questionTypes = ["MC", "MCM", "ACIQ", "ACMC", "CT", "ACIQM", "UVI", "LAR", "CTA"]; // Example options for question type

const hintTypes = ["audio", "text"];

function UnitDetailView() {
  const { courseId, unitId } = useParams();
  const [unitData, setUnitData] = useState(null);
  const [levels, setLevels] = useState([]);
  const [courseData, setCourseData] = useState({});
  const [selectedVoice, setSelectedVoice] = useState("");

  const isOptionCorrect = (option, answer, type) => {
    let result = false;

    switch (type) {
      case 'MC': {
        result = option?.toLowerCase() === answer.toLowerCase();
        break;
      }

      case 'ACIQ': {
        result = option?.toLowerCase() === answer.toLowerCase();
        break;
      }
      case 'ACIQM': {
        result = answer?.split('; ').includes(option);
        break;
      }

      case 'MCM': {
        result = answer?.split('; ').includes(option);
        break;
      }
      default:
        console.log("Unknown type", type);
    }
    return result
  };

  useEffect(() => {
    const fetchUnitDetails = async () => {
      try {
        const { data } = await getUnitDetails(courseId, unitId);
        setCourseData(data);
        setUnitData(data.unit);
        setLevels(data.levels);
        // setOriginalLevels(data.levels)
      } catch (error) {
        console.error('Failed to fetch unit details:', error);
      }
    };
    fetchUnitDetails();
  }, [courseId, unitId]);

  if (!unitData)
    return (
      <Backdrop open={!unitData} sx={{ zIndex: 1 }}>
        <CircularProgress />
      </Backdrop>
    ); // Loading state

  return (
    <ThemeProvider theme={ViewOnlyPages}>
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100%', width: '100%', backgroundColor: 'background.default', p: 4 }}>
          <Paper elevation={0} sx={{ padding: '2rem', width: '100%' }}>
            {/* Unit Information (Read-only) */}
            <Typography variant="h5" gutterBottom>Edit Unit: {unitData.name}</Typography>
            <Typography variant="subtitle1">Order: {unitData.order}</Typography>
            <Typography variant="subtitle1">Bonus: {unitData.isBonus ? 'Yes' : 'No'}</Typography>
            <FormControl size="small" sx={{ minWidth: 240, mt: 2 }}>
              <InputLabel shrink>Audio voice</InputLabel>
              <Select displayEmpty value={selectedVoice} label="Audio voice" renderValue={(value) => value || "Legacy fallback"} onChange={(event) => setSelectedVoice(event.target.value)}>
                <MenuItem value="">Legacy fallback</MenuItem>
                {getCourseVoiceEntries(courseData).map(([voice, config]) => <MenuItem key={voice} value={voice}><Avatar src={config.characterUrl} sx={{ width: 24, height: 24, mr: 1, bgcolor: config.characterColor }}>{voice[0]}</Avatar>{voice}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Level Sections */}
            {levels.map((level, levelIndex) => (
              <Paper
                key={level._id}
                sx={{
                  mt: 3,
                  mb: 3,
                  padding: '24px',
                  border: '1px solid silver',
                }}
              >
                <Grid2 container sx={{ padding: "24px" }}>
                  {/* Editable Level Name */}
                  <Grid2 item size={{ xs: 12, md: 12 }}>
                    <Typography sx={{ mt: 2, mb: 2 }} variant="h6">Level {level.order}</Typography>
                  </Grid2>

                  <Grid2 item size={{ xs: 12, md: 12 }}>
                    <TextField
                      label="Level Name"
                      variant="outlined"
                      fullWidth
                      value={level.name}
                      disabled
                      multiline
                    />
                  </Grid2>

                  {/* Questions */}
                  {level.questions.map((question, questionIndex) => (
                    question.isQuestionArchived ? null : (
                      <>
                        <br />
                        <Typography sx={{ mt: 2, mb: 2 }} variant="h6">Question: {question.order}</Typography>
                        <br />
                        <Grid2 container spacing={2} size={12} key={question._id}>
                          <Grid2 item size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth>
                              <InputLabel>Question Type</InputLabel>
                              <Select
                                value={question.questionType || ""}
                                label="Question Type"
                                disabled
                              >
                                {questionTypes.map((type) => (
                                  <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid2>

                          <Grid2 item size={{ xs: 12, md: 8 }}>
                            <TextField
                              label="Question"
                              variant="outlined"
                              fullWidth
                              value={question.question}
                              disabled
                              multiline
                            />
                          </Grid2>

                          {
                            ['ACIQ', 'CTA', 'CT'].includes(question.questionType) &&
                            <div className='audio-container'>
                              <VoiceAudioControl courseId={courseId} question={question} kind="question" voiceName={selectedVoice} readOnly />
                            </div>
                          }

                          {/* Render Options in Table Form */}
                          {!['ACMC', 'UVI', 'CT', 'LAR'].includes(question.questionType) && question.options && <Grid2 item size={12}>
                            <Table sx={{ background: 'primary.lighterShade' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Option</TableCell>
                                  <TableCell>Is Correct Answer</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Array.isArray(question.options) && question.options.map((option, optionIndex) => (
                                  <TableRow key={option._id}>
                                    <TableCell>
                                      <TextField
                                        value={option}
                                        fullWidth
                                        disabled
                                        multiline
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Checkbox
                                        checked={isOptionCorrect(option, question.Answer, question.questionType)}
                                        disabled
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Grid2>}


                          {question && ['ACMC'].includes(question.questionType) ? <Grid2 item>
                            <Table sx={{ background: 'primary.lighterShade' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Option</TableCell>
                                  <TableCell>Audio Clip</TableCell>
                                  <TableCell>Is Correct Answer</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(question.options || {}).map(([optionName], optionIndex) => (
                                  <TableRow key={optionName}>
                                    <TableCell>
                                      {optionName}
                                    </TableCell>
                                    <TableCell>
                                      <VoiceAudioControl courseId={courseId} question={question} kind="option" optionKey={optionName} voiceName={selectedVoice} readOnly />
                                    </TableCell>
                                    <TableCell>
                                      <Checkbox
                                        checked={question.Answer === optionName}
                                        disabled
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Grid2> : null
                          }

                          {question.noteBrief &&
                            <Grid2 item size={{ xs: 12, md: 6 }}>
                              <TextField
                                label="Note Brief"
                                variant="outlined"
                                fullWidth
                                value={question.noteBrief}
                                disabled
                                multiline
                              />
                            </Grid2>
                          }

                          {question.noteFull &&
                            <Grid2 item size={{ xs: 12, md: 6 }}>
                              <TextField
                                label="Note Full"
                                variant="outlined"
                                fullWidth
                                value={question.noteFull}
                                disabled
                                multiline
                              />
                            </Grid2>
                          }

                          {(question.questionType === "UVI" || question.questionType === "LAR") &&
                            <Grid2 item size={{ xs: 12, md: 4 }}>
                              <TextField
                                label="Phrase"
                                variant="outlined"
                                fullWidth
                                value={question.phrase}
                                disabled
                                multiline
                              />
                            </Grid2>
                          }

                          {question.hintType &&
                            <Grid2 item size={{ xs: 12, md: 4 }}>
                              <FormControl fullWidth>
                                <InputLabel>Hint Type</InputLabel>
                                <Select
                                  value={question.hintType}
                                  label="Hint Type"
                                  disabled
                                >
                                  {hintTypes.map((type) => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid2>
                          }

                          {question.hintType === "text" && <Grid2 item size={{ xs: 12, md: 8 }}>
                            <TextField
                              label="Hint"
                              variant="outlined"
                              fullWidth
                              value={question.hint}
                              disabled
                              multiline
                            />
                          </Grid2>}

                          {question.hintType === "audio" ? <div>
                            <Grid2 item size={{ xs: 12, md: 8 }}>
                              <InputLabel sx={{ marginLeft: 2, marginTop: 1 }}>Hint Audio</InputLabel>
                            </Grid2>
                            <VoiceAudioControl courseId={courseId} question={question} kind="hint" voiceName={selectedVoice} readOnly />
                          </div> : ""}


                          <Grid2 item size={{ xs: 12, md: 12 }}>
                            <Divider
                              sx={{
                                borderBottomWidth: 3,  // Set the thickness
                                borderColor: 'primary.main',
                                mt: 4,
                                mb: 4   // Set the color to blue
                              }}
                            />
                          </Grid2>
                        </Grid2>
                      </>
                    )
                  ))}
                </Grid2>
              </Paper>
            ))}
          </Paper>
        </Box>
      </Layout>
    </ThemeProvider>
  )
}

export default UnitDetailView
